import React, { useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { KTSVG } from '../../../_myb/helpers';
import { useAppContext } from '../apps/AppContext';
import { BizList, BranchDetails, CreateBizDTO } from '../../types';
import { createBizForUser, createBranch, fetchBizListForUser, fetchIpData, switchBiz } from '../../api';
import clsx from 'clsx';
import { EasyPicker } from '../widgets/components/SimplePicker';
import { businessTypes } from '../auth/components/Registration';
import { CountryPicker } from './Country';
import { CurrencyPicker } from './Currency';
import { CountryList } from '../../DS-Files';
import CustomModal from '../widgets/components/CustomModal';
import { toast } from 'react-toastify';
import { AuthModel, useAuth } from '../auth';
import { getUserByToken } from '../auth/core/_requests';
import { brand, brandSettings } from './BrandSettings';
import { trackEvent, AnalyticsEvent } from '../apps/analytics';

const LAST_BIZ_KEY = 'lastSelectedBiz';

const getStoredBizPreference = (userEmail: string): { bizId?: string; branchId?: string } | null => {
  try {
    const stored = localStorage.getItem(`${LAST_BIZ_KEY}_${userEmail}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading stored biz preference:', error);
    return null;
  }
};

const storeBizPreference = (userEmail: string, bizId: string, branchId?: string) => {
  try {
    localStorage.setItem(`${LAST_BIZ_KEY}_${userEmail}`, JSON.stringify({ bizId, branchId }));
  } catch (error) {
    console.error('Error storing biz preference:', error);
  }
};


const SEARCH_THRESHOLD = 12;

const BizPicker: React.FC = () => {
  const { bizMaster } = useAppContext();
  const { saveAuth, setCurrentUser, switchToken } = useAuth()
  const [bizList, setBizList] = useState<BizList[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [createBiz, setCreateBiz] = useState<boolean>(false)
  const [createBranchModal, setCreateBranchModal] = useState<boolean>(false)
  const [refreshKey, setRefreshKey] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  // Right after login the first fetch can fail or come back empty (auth/context not settled yet) and the
  // picker mounted once, so the list stayed empty for minutes. Callers retry with forceRefresh (bypasses the
  // client cache) when the list is still empty, e.g. on dropdown open or once bizMaster finishes loading.
  const getBizList = async (forceRefresh?: boolean) => {
    setIsLoading(true);
    try {
      const bizListResult = await fetchBizListForUser(forceRefresh);
      if (bizListResult) {
        const sortedList = [...bizListResult].sort((a, b) => a.bizId.localeCompare(b.bizId));
        setBizList(sortedList);
      }
    } catch (error) {
      console.error(`Biz Picker Fetch Error:${JSON.stringify(error, null, 2)}`)
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getBizList(refreshKey > 0);
  }, [refreshKey]);

  useEffect(() => {
    if (bizMaster?.bizId && bizList.length === 0) getBizList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizMaster?.bizId]);

  const handleCreateBiz = () => {
    setCreateBiz(true)
  }

  const closeCreateBiz = () => {
    setCreateBiz(false)
  }

  const closeCreateBranch = () => setCreateBranchModal(false)

  // MODIFY handleBizChange function to store preference
  const handleBizChange = async (biz: BizList) => {
    const auth: AuthModel = {
      api_token: '',
      refreshToken: undefined
    }
    const result = await switchBiz(biz.bizId, biz.branchId)
    if (result) {
      auth.api_token = result.token;
      saveAuth(auth);
      const user = await getUserByToken(auth.api_token);
      // Mirrors BranchSettings.tsx's branch-switch pattern (MBAI-547) — resets biz-scoped
      // context (bizMaster/roleAccess/subscriptionData) and swaps the current user without
      // a visible logout or a full page reload.
      await switchToken();
      setCurrentUser(user);

      // NEW: Store the biz preference using email
      if (user?.email) {
        storeBizPreference(user.email, biz.bizId, biz.branchId);
      }
    }
  };

  const filteredBizList = searchTerm
    ? bizList.filter((biz) => biz.bizName?.toLowerCase().includes(searchTerm.toLowerCase()))
    : bizList;

  return (
    <>
      <Dropdown onToggle={(isOpen) => {
        if (!isOpen) setSearchTerm('')
        else if (bizList.length === 0 && !isLoading) getBizList(true)
      }}>
        <Dropdown.Toggle
          className="border border-secondary bg-secondary"
          variant="outline"
          id="dropdown-basic"
        >
          <KTSVG
            path="media/icons/duotune/ecommerce/ecm004.svg"
            className="svg-icon-muted svg-icon-2hx me-2"
          />
          {bizMaster?.bizDetails?.bizName || 'Your Business Name'.toTranslatedDisplay()}
        </Dropdown.Toggle>

        <Dropdown.Menu style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
          {bizList.length > SEARCH_THRESHOLD && (
            <div className='px-3 pb-2' onClick={(e) => e.stopPropagation()}>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={'Search business...'.toTranslatedDisplay()}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredBizList.length > 0 ? (
            filteredBizList.map((biz) => (
              <Dropdown.Item className={` ${biz.hasBranch && 'fw-bolder'}  ${(biz.branchId) ? 'ms-3' : ''}`} key={biz.bizId} onClick={() => handleBizChange(biz)}>
                {biz.bizName}
              </Dropdown.Item>
            ))
          ) : (
            <Dropdown.ItemText className='text-muted text-center'>{(isLoading ? 'Loading...' : 'No results').toTranslatedDisplay()}</Dropdown.ItemText>
          )}
          <Dropdown.Divider />
          {brandSettings.showBizCreation !== false &&
            <Dropdown.Item key={'createBiz'} onClick={handleCreateBiz}>
              <KTSVG path="media/icons/duotune/general/gen035.svg" className="svg-icon-muted svg-icon-hx me-2" />
              {'Create Business'.toTranslatedDisplay()}
            </Dropdown.Item>
          }
          {brandSettings.showBranchCreation !== false &&
            <Dropdown.Item key={'createBranch'} onClick={() => setCreateBranchModal(true)}>
              <KTSVG path="media/icons/duotune/general/gen035.svg" className="svg-icon-muted svg-icon-hx me-2" />
              {'Create Branch'.toTranslatedDisplay()}
            </Dropdown.Item>
          }
        </Dropdown.Menu>
      </Dropdown>
      <CustomModal
        title={'Create Business'.toTranslatedDisplay()}
        isVisible={createBiz}
        onClose={closeCreateBiz}
        body={<CreateBizForm onClose={closeCreateBiz} onSave={() => { closeCreateBiz(); setRefreshKey(k => k + 1); }} />}
        size='small'
        id="createBiz"
        onSave={closeCreateBiz}
      />
      <CustomModal
        title={'Create Branch'.toTranslatedDisplay()}
        isVisible={createBranchModal}
        onClose={closeCreateBranch}
        body={<CreateBranchForm onClose={closeCreateBranch} onSave={() => { closeCreateBranch(); setRefreshKey(k => k + 1); }} />}
        size='small'
        id="createBranch"
        onSave={closeCreateBranch}
      />
    </>
  );
};

export default BizPicker;

interface CreateBizFormProps {
  onSave?: () => void;
  onClose?: () => void;
}

const CreateBranchForm: React.FC<CreateBizFormProps> = ({ onSave, onClose }) => {
  const [branchName, setBranchName] = useState<string>('')
  const [error, setError] = useState<string>('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchName.trim()) {
      setError('Branch Name is required.'.toTranslatedDisplay());
      return;
    }
    try {
      await createBranch(new BranchDetails({ branchName }));
      toast.success('Branch Created Successfully!'.toTranslatedDisplay());
      if (onSave) onSave();
    } catch (err: any) {
      toast.error('Error: '.toTranslatedDisplay() + err.message);
    }
  };

  return (
    <form className="col">
      <div className="mb-3">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Branch Name'.toTranslatedDisplay()}</label>
        <input
          placeholder={'Your branch name'.toTranslatedDisplay()}
          value={branchName}
          onChange={(e) => { setBranchName(e.target.value); setError(''); }}
          type="text"
          autoComplete="off"
          autoFocus
          className={clsx('form-control bg-transparent', { 'is-invalid': error })}
        />
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
      <div className="d-flex justify-content-end">
        <button onClick={handleSubmit} className="btn btn-primary">{'Create Branch'.toTranslatedDisplay()}</button>
        <button type="button" onClick={onClose} className="ms-2 btn btn-secondary">{'Cancel'.toTranslatedDisplay()}</button>
      </div>
    </form>
  );
};

const CreateBizForm: React.FC<CreateBizFormProps> = ({ onSave, onClose }) => {
  const [errors, setErrors] = useState<{ bizName?: string }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createBizDto, setCreateBizDto] = useState<CreateBizDTO>({
    bizName: '',
    country: '',
    currency: '',
    bizType: '',
    timeZone: '',
  });

  useEffect(() => {
    fetchIpData().then((ipData) => {
      if (ipData?.country) {
        setCreateBizDto((prev) => ({ ...prev, country: ipData.country }));
      }
    });
  }, []);

  useEffect(() => {
    if (!createBizDto.country) return;
    const found = CountryList.find((c) => c.isoCode === createBizDto.country);
    if (found) {
      setCreateBizDto((prev) => ({
        ...prev,
        currency: found.currencyCode,
        timeZone: found.timeZone,
      }));
    }
  }, [createBizDto.country]);

  // Generic onChange handler for text inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreateBizDto((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Specific handlers for pickers
  const handlePickerChange = (field: keyof CreateBizDTO, value: string) => {
    setCreateBizDto((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;
    const newErrors: { bizName?: string } = {};
    try {
      // Validation for business name
      if (!createBizDto?.bizName?.trim()) {
        newErrors.bizName = 'Business Name is required.'.toTranslatedDisplay();
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setIsSaving(true);
      const reponse = await createBizForUser({ ...createBizDto, brand })
      trackEvent(AnalyticsEvent.BizCreated)
      toast.success('Business Created Successfully!'.toTranslatedDisplay())
      if (onSave) onSave()
    } catch (error: any) {
      toast.error('Error :'.toTranslatedDisplay() + error.message)
    } finally {
      setIsSaving(false);
    }

  };

  return (
    <form className="col">
      <div className="mb-3">
        <label className="form-label fw-bolder text-gray-900 fs-6">{'Business Name'.toTranslatedDisplay()}</label>
        <input
          placeholder={'Your business name'.toTranslatedDisplay()}
          value={createBizDto?.bizName}
          name="bizName"
          onChange={handleInputChange}
          type="text"
          autoComplete="off"
          className={clsx('form-control bg-transparent', { 'is-invalid': errors.bizName })}
        />
        {errors.bizName && (
          <div className="invalid-feedback">{errors.bizName}</div>
        )}
      </div>

      <div className="mb-3">
        <EasyPicker
          name="bizType"
          value={createBizDto.bizType}
          label={'Business Type'.toTranslatedDisplay()}
          options={businessTypes} // Assume businessTypes is an array of options
          onSelectionChange={(e) => handlePickerChange('bizType', e.target.value)}
          placeholder={'Select business type'.toTranslatedDisplay()}
        />
      </div>

      <div className="mb-3">
        <CountryPicker
          label={'Country'.toTranslatedDisplay()}
          value={createBizDto.country}
          onSelectionChange={(countryId) =>
            handlePickerChange('country', countryId)
          }
        />
      </div>

      <div className="mb-3">
        <CurrencyPicker
          label={'Business Currency'.toTranslatedDisplay()}
          selectedCurrency={createBizDto.currency}
          onSelectionChange={(currencyId) =>
            handlePickerChange('currency', currencyId)
          }
        />
      </div>

      <div className="d-flex justify-content-end">
        <button onClick={handleSubmit} disabled={isSaving} className="btn btn-primary">{(isSaving ? 'Creating...' : 'Create Business').toTranslatedDisplay()}</button>
        <button type="button" onClick={onClose} className="ms-2 btn btn-secondary">{'Cancel'.toTranslatedDisplay()}</button>
      </div>
    </form>
  );
};




